import { api } from './api';
import type {
  Tenant,
  User,
  BillingStatus,
  BillingMode,
  FeatureFlags,
  QuotaOverrides,
  Tier,
} from '@traaker/shared';

export interface CreateTenantPayload {
  name: string;
  domain: string;
  tier: Tier;
  ownerEmail: string;
  ownerName?: string;
  /** Default UI language for the owner (seeds onboarding + app language on first login). */
  ownerUiLanguage?: 'fr' | 'en';
  settings?: {
    quotaOverrides?: QuotaOverrides;
    featureFlags?: FeatureFlags;
    enabledEngines?: string[];
  };
  billing: {
    mode: BillingMode;
    amountCents?: number;
    interval?: 'month' | 'year';
    notes?: string;
  };
  /** Non-agency time-trial length in days; 0/omitted ⇒ tenant stays pending. */
  trialDays?: number;
  agency?: {
    monthlyAllocation: number;
    pricePerCreditCents?: number;
    startingCredits?: number;
    /** Pay-first ordering flow — seed `pending` + force checkout. Requires planKey. */
    payFirst?: boolean;
    /** Agency plan charged in pay-first mode (agency_s_monthly | _m_ | _l_). */
    planKey?: string;
  };
}

export interface UpdateTenantPayload {
  name?: string;
  domain?: string;
  tier?: Tier;
  settings?: {
    quotaOverrides?: QuotaOverrides;
    featureFlags?: FeatureFlags;
    enabledEngines?: string[];
  };
  contractAmountCents?: number;
  contractInterval?: 'month' | 'year';
  contractNotes?: string;
  billingMode?: BillingMode;
}

export const adminService = {
  listTenants: () => api.get<Tenant[]>('/admin/tenants'),
  /**
   * One page of tenants, newest-first. Backed by the `all-tenants-index` GSI.
   * Pass `cursor` (a previous page's `nextCursor`) to page forward.
   */
  listTenantsPage: (opts: { limit?: number; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(opts.limit ?? 50));
    if (opts.cursor) qs.set('cursor', opts.cursor);
    return api.get<{ items: Tenant[]; nextCursor: string | null }>(
      `/admin/tenants?${qs.toString()}`,
    );
  },
  getTenant: (tenantId: string) =>
    api.get<{ tenant: Tenant; users: User[] }>(`/admin/tenants/${tenantId}`),
  createTenant: (payload: CreateTenantPayload) =>
    api.post<{ tenant: Tenant; user: User }>('/admin/tenants', payload),
  updateTenant: (tenantId: string, payload: UpdateTenantPayload) =>
    api.patch(`/admin/tenants/${tenantId}`, payload),
  setBillingStatus: (tenantId: string, status: BillingStatus) =>
    api.patch(`/admin/tenants/${tenantId}/billing-status`, { status }),
  endTrial: (tenantId: string) =>
    api.post<{ success: boolean }>(`/admin/tenants/${tenantId}/end-trial`, {}),
  extendTrial: (tenantId: string, trialEndsAt: string) =>
    api.post<{ success: boolean }>(`/admin/tenants/${tenantId}/extend-trial`, { trialEndsAt }),
  setTrialTier: (tenantId: string, tier: Tier) =>
    api.post<{ success: boolean }>(`/admin/tenants/${tenantId}/trial-tier`, { tier }),
  setFeatureFlags: (tenantId: string, flags: FeatureFlags) =>
    api.patch(`/admin/tenants/${tenantId}/feature-flags`, flags),
  createCheckoutSession: (
    tenantId: string,
    payload: { successUrl: string; cancelUrl: string },
  ) => api.post<{ url: string }>(`/admin/tenants/${tenantId}/checkout-session`, payload),
  ensureStripeCustomer: (tenantId: string) =>
    api.post<{ stripeCustomerId: string }>(`/admin/tenants/${tenantId}/stripe-customer`, {}),
  resendWelcomeEmail: (tenantId: string) =>
    api.post<{ sent: boolean; to: string }>(`/admin/tenants/${tenantId}/resend-welcome`, {}),
  resetPassword: (tenantId: string, email?: string) =>
    api.post<{ sent: boolean; to: string }>(`/admin/tenants/${tenantId}/reset-password`, { email }),
  deleteTenant: (tenantId: string, confirmationName: string) =>
    api.delete<{ deleted: boolean; tenantId: string; usersDeleted: number }>(
      `/admin/tenants/${tenantId}`,
      { confirmationName },
    ),
  getCredits: (tenantId: string) =>
    api.get<{ subscriptionCredits: number; topupCredits: number; reserved: number; available: number }>(
      `/admin/tenants/${tenantId}/credits`,
    ),
  adjustCredits: (tenantId: string, amount: number, reason: string) =>
    api.post<{ subscriptionCredits: number; topupCredits: number; reserved: number; available: number }>(
      `/admin/tenants/${tenantId}/credits/adjust`,
      { amount, reason },
    ),
};

/** Customer-facing self-serve billing (Stripe Customer Portal). */
export const billingService = {
  createPortalSession: (returnUrl?: string) =>
    api.post<{ url: string }>('/billing/portal-session', { returnUrl }),
  /** Self-serve subscription checkout (basic/pro/elite). Returns a Stripe Checkout URL. */
  createSelfServeCheckout: (tier: string, interval: 'month' | 'year' = 'month') =>
    api.post<{ checkoutUrl: string }>('/billing/checkout/self-serve', { tier, interval }),
};
